import { useState } from 'react';
import { useLocation } from 'react-router-dom';

export type DocumentNavigationType =
  | 'navigate'
  | 'reload'
  | 'back_forward'
  | 'prerender'
  | 'unknown';

export type DeeplinkParam = 'pageState' | 'filters' | 'filterGroups';

export type DocumentArrival = {
  /** False once this param has already been counted for the document. */
  isDocumentArrival: boolean;
  navigation_type: DocumentNavigationType;
  /**
   * Hostname of `document.referrer` — the app the link was followed from — or
   * '' when there is none. Never the path or query.
   */
  referrer_host: string;
};

let arrivalLocationKey: string | null = null;
const countedParams = new Set<DeeplinkParam>();

const readNavigationType = (): DocumentNavigationType => {
  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return 'unknown';
  }
  const entry = performance.getEntriesByType('navigation')[0];
  // Widened to string: the lib typings predate `prerender`.
  const type: string | undefined =
    entry && 'type' in entry
      ? (entry as PerformanceNavigationTiming).type
      : undefined;
  switch (type) {
    case 'navigate':
    case 'reload':
    case 'back_forward':
    case 'prerender':
      return type;
    default:
      return 'unknown';
  }
};

const readReferrerHost = (): string => {
  if (typeof document === 'undefined' || !document.referrer) return '';
  try {
    return new URL(document.referrer).hostname;
  } catch {
    return '';
  }
};

/**
 * Remembers the location the document arrived at. Every in-app navigation
 * mints a new key — a page switch as much as the URL sync writing a param back
 * with `replace` — so nothing that mounts under a later key can be an arrival.
 * It has to be recorded at the app root, before any route renders: most
 * sessions start on Landing, where no deserialiser renders, and the first
 * analytics page reached from there is an in-app visit, not the arrival.
 * Idempotent — only the first call counts.
 */
export const recordDocumentArrival = (locationKey: string): void => {
  arrivalLocationKey ??= locationKey;
};

/** Records the router location the app root first renders under. */
export const useRecordDocumentArrival = (): void => {
  const { key } = useLocation();
  recordDocumentArrival(key);
};

/**
 * Whether `locationKey` is the document's arrival location. False until the
 * root has recorded one — missing an arrival beats inventing one.
 */
export const isArrivalLocation = (locationKey: string): boolean =>
  arrivalLocationKey !== null && locationKey === arrivalLocationKey;

/**
 * Decided once, at the caller's first render: did it mount at the arrival
 * location with `hasParam` already true? Both facts have to be read at mount.
 * The filter deserialiser lives on across analytics template switches and
 * re-parses whenever the URL sync rewrites its param, and none of that is an
 * arrival — while a parse that only completes later, once taxonomy versions
 * have loaded, still is.
 */
export const useMountedOnArrivalWith = (hasParam: boolean): boolean => {
  const { key } = useLocation();
  const [mountedOnArrival] = useState(() => isArrivalLocation(key) && hasParam);
  return mountedOnArrival;
};

/**
 * The arrival for `param`, handed out once per document.
 *
 * `?pageState=` / `?filters=` are written back to the URL during normal use
 * and carried across in-app page switches, so a route mounting with one says
 * nothing on its own about how the user got there. Only a mount that
 * `useMountedOnArrivalWith` vouches for is an arrival from outside — a link,
 * a bookmark or a reload, which `navigation_type` tells apart — and this
 * one-shot keeps a later remount at the same location (browser back to the
 * entry) from counting it again.
 */
export const claimDocumentArrival = (param: DeeplinkParam): DocumentArrival => {
  const isDocumentArrival = !countedParams.has(param);
  countedParams.add(param);
  return {
    isDocumentArrival,
    navigation_type: readNavigationType(),
    referrer_host: readReferrerHost(),
  };
};
